import { useMutation } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AnalyzeRequest, AnalyzeResponse } from "../types/domain";

/**
 * Normalize the request before sending: an empty `expectedChars` array is
 * indistinguishable from "no expectation" at the backend, but the wire
 * payload shouldn't carry an empty list when the caller meant "skip".
 */
function normalizeAnalyzeRequest(payload: AnalyzeRequest): AnalyzeRequest {
  if (Array.isArray(payload.expectedChars) && payload.expectedChars.length === 0) {
    const { expectedChars: _ignored, ...rest } = payload;
    void _ignored;
    return rest;
  }
  return payload;
}

/**
 * Run a per-image analyze request.
 *
 * The hook deliberately does NOT auto-navigate. With multi-image uploads the
 * caller wants to drive navigation itself (typically only on the first
 * success) and silently persist the rest; auto-navigation on every success
 * would yank the user away from the page before the batch finishes.
 */
export function useAnalyze() {
  return useMutation<AnalyzeResponse["data"], Error, AnalyzeRequest>({
    mutationFn: async (payload) => {
      return api<AnalyzeResponse["data"]>("/practice/analyze", {
        method: "POST",
        body: normalizeAnalyzeRequest(payload),
      });
    },
  });
}